import { Combobox, type ComboboxOption } from '@/components/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import type { AllocateFillFormState } from '../lib/allocate-fill-state';
import { RAISE_CURRENCIES } from '../lib/raise-form-schema';
import { TICKER_LABEL } from '../lib/risk-summary-derivation';
import { ContractReferenceField } from './allocate-fill-reference-field';

// Show the full metal name (Copper, Aluminum, …) but keep the LME code as the value.
const METAL_OPTIONS: ComboboxOption[] = ['CU', 'AL', 'ZN', 'NI', 'PB', 'SN'].map((v) => ({
  value: v,
  label: TICKER_LABEL[v] ?? v,
}));
const DIRECTION_OPTIONS: ComboboxOption[] = ['Buy', 'Sell'].map((v) => ({ value: v, label: v }));
const CURRENCY_OPTIONS: ComboboxOption[] = RAISE_CURRENCIES.map((v) => ({ value: v, label: v }));

export interface AllocateFillTopFieldsProps {
  isFx: boolean;
  currencyPairs: string[];
  state: AllocateFillFormState;
  set: (key: keyof AllocateFillFormState, value: string) => void;
}

export function AllocateFillTopFields(props: Readonly<AllocateFillTopFieldsProps>) {
  return (
    <div className="flex flex-col gap-3">
      <FilterRow {...props} />
      <FillRow {...props} />
      <MetaRow {...props} />
      <NotesRow {...props} />
    </div>
  );
}

function FilterRow({ isFx, currencyPairs, state, set }: Readonly<AllocateFillTopFieldsProps>) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {isFx ? (
        <SelectField
          label="Currency pair"
          value={state.currencyPair}
          options={currencyPairs.map((v) => ({ value: v, label: v }))}
          onChange={(v) => {
            set('currencyPair', v);
          }}
        />
      ) : (
        <SelectField
          label="Metal"
          value={state.metal}
          options={METAL_OPTIONS}
          onChange={(v) => {
            set('metal', v);
          }}
        />
      )}
      <SelectField
        label="Direction"
        value={state.direction}
        options={DIRECTION_OPTIONS}
        onChange={(v) => {
          set('direction', v);
        }}
      />
    </div>
  );
}

function FillRow({ isFx, state, set }: Readonly<AllocateFillTopFieldsProps>) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Field
        label={isFx ? 'Total notional' : 'Total fill (MT)'}
        testId="allocate-fill-total"
        value={state.totalQuantity}
        inputMode="decimal"
        onChange={(v) => {
          set('totalQuantity', v);
        }}
      />
      <Field
        label={isFx ? 'FX rate' : 'Executed price'}
        testId="allocate-fill-price"
        value={state.executedPrice}
        inputMode="decimal"
        onChange={(v) => {
          set('executedPrice', v);
        }}
      />
      <SelectField
        label="Currency"
        value={state.executedPriceCurrency}
        options={CURRENCY_OPTIONS}
        clearable={false}
        onChange={(v) => {
          set('executedPriceCurrency', v);
        }}
      />
    </div>
  );
}

function MetaRow({ isFx, state, set }: Readonly<AllocateFillTopFieldsProps>) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
      <Field
        label="Execution date"
        testId="allocate-fill-date"
        value={state.executionDate}
        type="date"
        onChange={(v) => {
          set('executionDate', v);
        }}
      />
      <Field
        label={isFx ? 'Value date' : 'Prompt date'}
        testId="allocate-fill-prompt-date"
        value={state.promptDate}
        type="date"
        onChange={(v) => {
          set('promptDate', v);
        }}
      />
      {/* FX allocations are OTC — the exchange is fixed on the payload, so no field. */}
      {!isFx && (
        <Field
          label="Exchange"
          value={state.exchange}
          onChange={(v) => {
            set('exchange', v);
          }}
        />
      )}
      <Field
        label="Broker"
        value={state.brokerName}
        onChange={(v) => {
          set('brokerName', v);
        }}
      />
    </div>
  );
}

function NotesRow({ isFx, state, set }: Readonly<AllocateFillTopFieldsProps>) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <ContractReferenceField isFx={isFx} state={state} set={set} />
      <div className="flex flex-col gap-1">
        <Label>Notes</Label>
        <Textarea
          rows={2}
          value={state.notes}
          onChange={(event) => {
            set('notes', event.target.value);
          }}
        />
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: 'text' | 'date';
  inputMode?: 'decimal' | 'text';
  maxLength?: number;
  placeholder?: string;
  testId?: string;
}

function Field({ label, value, onChange, ...rest }: Readonly<FieldProps>) {
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Input
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        type={rest.type ?? 'text'}
        inputMode={rest.inputMode}
        maxLength={rest.maxLength}
        placeholder={rest.placeholder}
        data-testid={rest.testId}
      />
    </div>
  );
}

interface SelectFieldProps {
  label: string;
  value: string;
  options: readonly ComboboxOption[];
  onChange: (value: string) => void;
  clearable?: boolean;
  placeholder?: string;
}

function SelectField({
  label,
  value,
  options,
  onChange,
  clearable = true,
  placeholder = 'Any',
}: Readonly<SelectFieldProps>) {
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Combobox
        aria-label={label}
        options={options}
        value={value === '' ? undefined : value}
        onChange={(next) => {
          onChange(next ?? '');
        }}
        placeholder={placeholder}
        clearable={clearable}
        className="h-10"
      />
    </div>
  );
}
